import { useEffect } from 'react';

interface ArticleMeta {
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  ogType?: string;
  ogImage?: string;
  noIndex?: boolean;
  schema?: object | object[];
  articleMeta?: ArticleMeta;
}

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://callofdutynext.com';
const SITE_NAME = 'CallOfDutyNext.com';
const TWITTER_HANDLE = '@CallOfDutyNext';

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  noIndex = false,
  schema,
  articleMeta,
}: SEOHeadProps) {
  const fullCanonical = `${SITE_URL}${canonical}`;

  useEffect(() => {
    if (!schema) return;
    const schemas = Array.isArray(schema) ? schema : [schema];
    const ids: string[] = [];

    schemas.forEach((s, i) => {
      const id = `ld-json-${canonical.replace(/\//g, '-').replace(/^-/, '') || 'home'}-${i}`;
      ids.push(id);
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.text = JSON.stringify(s);
      document.head.appendChild(script);
    });

    return () => {
      ids.forEach((id) => document.getElementById(id)?.remove());
    };
  }, [schema, canonical]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={fullCanonical} />
      <meta name="Last-modified" content={new Date().toISOString().slice(0, 10)} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Article-specific Open Graph tags */}
      {ogType === 'article' && articleMeta?.publishedTime && (
        <meta property="article:published_time" content={articleMeta.publishedTime} />
      )}
      {ogType === 'article' && articleMeta?.modifiedTime && (
        <meta property="article:modified_time" content={articleMeta.modifiedTime} />
      )}
      {ogType === 'article' && articleMeta?.author && (
        <meta property="article:author" content={articleMeta.author} />
      )}
      {ogType === 'article' && articleMeta?.section && (
        <meta property="article:section" content={articleMeta.section} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}

export { SITE_URL, SITE_NAME };
